import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { createBreakpoint } from "react-use";

import { getChainName } from "config/chains";
import { isDevelopment } from "config/env";
import { useGlobalContext } from "context/GlobalContext/GlobalContextProvider";
import { useChainId } from "lib/chains";
import { useENS } from "lib/legacy";
import { ChainId } from "lib/node/types";
import { sendUserAnalyticsConnectWalletClickEvent } from "lib/userAnalytics";
import { shortenAddressOrEns } from "lib/wallets";
import useWallet from "lib/wallets/useWallet";

import { NectarliquidLogo } from "./NectarliquidLogo";

interface HomeScreenProps {
  selectedScanChainId: ChainId;
  hasSelectedSourceChain: boolean;
  onSelectSourceChain: (chainId: ChainId) => void;
  onScanWallet: () => void;
  onClose: () => void;
}

const supportedChains = isDevelopment()
  ? [
      {
        chainId: "84532" as ChainId,
        title: "Base",
        icon: "/assets/Base.png",
        tokens: ["USDC"],
      },
      {
        chainId: "421614" as ChainId,
        title: "Arbitrum",
        icon: "/assets/arbitrium.svg",
        tokens: ["XRWA", "WNT"],
      },
      {
        chainId: "200810" as ChainId,
        title: "Bitlayer",
        icon: "/assets/bitlayer.png",
        tokens: ["BTC"],
      },
    ]
  : [
      {
        chainId: "56" as ChainId,
        title: "Binance",
        icon: "/assets/Binance-smart-chain.png",
        tokens: ["USDT"],
      },
      {
        chainId: "200901" as ChainId,
        title: "Bitlayer",
        icon: "/assets/bitlayer.png",
        tokens: ["BTC"],
      },
      {
        chainId: "42161" as ChainId,
        title: "Arbitrum",
        icon: "/assets/arbitrium.svg",
        tokens: ["XRWA", "WNT", "USDT", "WETH"],
      },
      {
        chainId: "8453" as ChainId,
        title: "Base",
        icon: "/assets/Base.png",
        tokens: ["USDC", "WETH"],
      },
      {
        chainId: "1" as ChainId,
        title: "Ethereum",
        icon: "/assets/Ethereum.png",
        tokens: ["USDC", "WETH", "USDT", "WBTC"],
      },
      {
        chainId: "42161" as ChainId,
        title: "Polygon",
        icon: "/assets/polygon.svg",
        tokens: ["WNT", "USDT", "USDC"],
      },
    ];

export function HomeScreen({
  selectedScanChainId,
  hasSelectedSourceChain,
  onSelectSourceChain,
  onScanWallet,
  onClose,
}: HomeScreenProps) {
  useLingui();
  const useBreakpoint = createBreakpoint({ L: 600, M: 550, S: 400 });
  const breakpoint = useBreakpoint();
  const { active, account } = useWallet();
  const { ensName } = useENS(account);
  const { chainId } = useChainId();
  const { setConnectWalletModalOpen } = useGlobalContext();
  const displayAddressLength = breakpoint === "S" ? 9 : 13;
  const address = ensName || account;
  const shortenedAddress = address && shortenAddressOrEns(address, displayAddressLength);
  const connectedNetworkName = getChainName(chainId);
  const isConnected = Boolean(active && account);

  const handleConnectWalletClick = () => {
    sendUserAnalyticsConnectWalletClickEvent("ActionButton");
    setConnectWalletModalOpen?.(true);
  };

  return (
    <div className="mt-[60px] flex flex-col items-center px-4 py-6 md:px-8 md:py-10">
      <div className="-mt-10 flex w-full  flex-1 flex-col items-center justify-center">
        <h1 className="text-center text-24 font-bold md:text-56">
          {/* text-3xl md:text-5xl lg:text-6xl text-foreground text-balance text-center font-bold tracking-tight */}
          <Trans>Nectarliquid Transfer</Trans>
        </h1>
        <p className="text-sm md:text-base mt-3 text-pretty text-center text-[#ffffff66]">
          <Trans>Seamlessly transfer funds from multiple chains to your Nectarliquid wallet</Trans>
        </p>

        <div className="my-5 flex md:mt-84">
          <div className="my-5 md:my-7">
            <NectarliquidLogo />
          </div>

          {isConnected && (
            <>
              {/* <p className="text-muted-foreground text-sm md:text-base text-pretty text-center">
              {"Your wallet "}
              <span className="font-mono text-[#00EC97]">
                {shortenedAddress ?? "Wallet"}
              </span>
              {" is already connected on"}
              <br />
              <span className="text-[#00EC97] font-semibold">
                {connectedNetworkName},
              </span>
              {" would you like to proceed?"}
            </p> */}

              <div className="mt-14 w-full max-w-2xl">
                <h2 className="text-center text-24 font-bold md:text-34"><Trans>Choose source chain</Trans></h2>
                {/* <p className="text-sm md:text-base mt-3 text-center text-[#ffffff66] text-pretty">
                Show only supported bridge routes so the user understands the wallet scan will happen
                inside the selected network context.
              </p> */}

                <div className="mt-10 flex max-h-[360px] flex-col gap-6 overflow-y-scroll md:max-h-[480px]">
                  {supportedChains.map((c) => {
                    const isSelected = hasSelectedSourceChain && c.chainId === selectedScanChainId;
                    const description = t`Bridge ${c.tokens.join(", ")} to your Nectarliquid wallet`;

                    return (
                      <button
                        key={c.chainId}
                        type="button"
                        onClick={() => onSelectSourceChain(c.chainId)}
                        className={`flex w-full items-center justify-between gap-6 rounded-[28px] border px-6 py-8 text-left transition-all md:px-10 md:py-10 ${
                          isSelected
                            ? "border-[#00EC97] bg-[#00EC97]/10"
                            : "border-[#ffffff1a] bg-[#1E1E1F]/30 hover:bg-[#1E1E1F]/50"
                        }`}
                        aria-pressed={isSelected}
                      >
                        <div className="flex items-center gap-6">
                          <div
                            className={`flex h-[62px] w-[62px] items-center justify-center rounded-[18px] ${
                              isSelected ? "bg-[#00EC97]/15" : "bg-[#ffffff05]"
                            }`}
                          >
                            <img
                              src={c.icon}
                              alt={t`${c.title} logo`}
                              className="h-30 w-30 rounded-[18px] object-contain"
                            />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-22 md:text-26 font-semibold">{c.title}</span>
                            <span className="text-sm md:text-base mt-1 text-[#ffffff66]">{description}</span>
                          </div>
                        </div>

                        <div>
                          <span
                            className={`rounded-full px-6 py-3 text-14 font-semibold uppercase transition-colors ${
                              isSelected ? "bg-[#00EC97] text-black" : "bg-[#ffffff1a] text-[#ffffff66]"
                            }`}
                          >
                            {isSelected ? <Trans>Selected</Trans> : <Trans>Ready</Trans>}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {hasSelectedSourceChain && (
                  <button
                    onClick={onScanWallet}
                    className="mt-10 h-[64px] w-[404px] rounded-full bg-[#00EC97] text-14 font-semibold text-black transition-all hover:brightness-110 active:scale-[0.98] md:text-16"
                    type="button"
                  >
                    <Trans>Scan wallet</Trans>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col items-center justify-center">
          {!isConnected && (
            <>
              <p className="text-muted-foreground text-sm md:text-base text-pretty text-center">
                <Trans>Your wallet is not connected. Please connect your wallet to continue.</Trans>
              </p>

              <button
                onClick={handleConnectWalletClick}
                className="mt-20 h-[64px] w-[284px] rounded-full bg-[#00EC97] text-14 font-semibold text-black transition-all hover:brightness-110 active:scale-[0.98] md:text-16"
              >
                <Trans>Connect wallet</Trans>
              </button>
            </>
          )}
        </div>
        {isConnected && (
          <div
            className="rounded-2xl mt-8 w-full max-w-lg border border-[#E5A00D]/40 bg-[#E5A00D]/10 px-4 py-4 text-center md:px-5 md:py-4"
            role="note"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-[#E5A00D]"><Trans>Note</Trans></p>
            <p className="text-sm leading-relaxed md:text-base mt-2 text-[#ffffffb3]">
              <span className="font-medium text-[#F0C14B]"><Trans>Warning:</Trans></span>{" "}<Trans>If bridging fails, install the</Trans>{" "}
              <a
                href="https://chrome.google.com/webstore/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge"
                target="_blank"
                rel="noopener noreferrer"
                className="focus-visible:rounded-sm font-medium text-[#F0C14B] underline underline-offset-2 transition-colors hover:text-[#FFD666] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F0C14B]"
              >
                OKX Wallet
              </a>{" "}
              <Trans>Chrome extension and try again.</Trans>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
